import { useQuery } from '@tanstack/react-query';
import { differenceInHours, fromUnixTime } from 'date-fns';

import { usePermissions, useSelectedSite } from '/src/hooks';
import { Section } from '../Section';
import { TrendMetric, TrendMetricSection } from '../TrendMetric';
import { TableHeader, TableRow, TableSkeleton } from '../TableSection';

import { webAnalyticsQueryForHomeDash } from '/src/features/web-analytics/queries';
import { pageRowFormatter } from '/src/features/web-analytics/pages/list';
import { round } from '/src/utils/math';
import { msToHumanDuration } from '/src/utils/date';
import { FEATURES } from '/src/features/_global/constants';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';
import { NotInstalledWall } from '/src/features/web-analytics/walls/NotInstalled';
import { OptInWall } from '/src/features/web-analytics/walls/OptIn';
import { TruncateWithTooltip } from '/src/components/TruncateWithTooltip';

import { ReactComponent as VisitorsIcon } from '@crazyegginc/hatch/dist/images/icon-people-outline.svg';

export function VisitorSection({ dateRange }) {
  return (
    <Section className="col-span-2">
      <Section.Header icon={VisitorsIcon} color="text-dodger-blue-500" to="/web-analytics">
        <span>Traffic & Engagement</span>
      </Section.Header>
      <Section.Body>
        <Content dateRange={dateRange} />
      </Section.Body>
    </Section>
  );
}

function Content({ dateRange }) {
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.WEB_ANALYTICS);
  const { selectedSite } = useSelectedSite();

  if (!canViewDashboard) {
    return (
      <div className="p-5">
        <DashboardPaywall feature={FEATURES.WEB_ANALYTICS} reason={reason} mini={true} />
      </div>
    );
  }

  if (!selectedSite) return null;

  if (!selectedSite.installed) {
    return (
      <div className="p-5">
        <NotInstalledWall mini={true} />
      </div>
    );
  }

  if (!selectedSite.webAnalyticsEnabled) {
    return (
      <div className="p-5">
        <OptInWall mini={true} />
      </div>
    );
  }

  return <VisitorContent dateRange={dateRange} />;
}

function VisitorContent({ dateRange }) {
  const { selectedSite } = useSelectedSite();

  const { data, isLoading } = useQuery({
    ...webAnalyticsQueryForHomeDash({
      siteId: selectedSite.id,
      presetDate: dateRange.special,
    }),
  });
  const numDays = differenceInHours(fromUnixTime(dateRange.end + 1), fromUnixTime(dateRange.start)) / 24;

  return (
    <>
      <TrendMetricSection>
        <TrendMetric
          text="Average unique visitors per day"
          value={data ? Math.round(data.analytics.uniqueVisitors.total / numDays) : null}
          oldValue={
            data
              ? Math.round((data.analytics.uniqueVisitors.total / 1 + data.analytics.uniqueVisitors.delta) / numDays)
              : null
          }
          loading={isLoading}
        />
        <TrendMetric
          text="Average session duration"
          value={data?.analytics.avgSessionDuration.total ?? 0}
          delta={data?.analytics.avgSessionDuration.delta}
          loading={isLoading}
          formatFn={(x) => msToHumanDuration(x)}
        />
        <TrendMetric
          text="Bounce rate"
          value={data ? round(data.analytics.bounceRate.total * 100, 1) : 0}
          delta={data?.analytics.bounceRate.delta}
          loading={isLoading}
          formatFn={(x) => `${x}%`}
          inverseColor={true}
        />
      </TrendMetricSection>
      <TopTraffic data={data} isLoading={isLoading} />
    </>
  );
}

function TopTraffic({ data, isLoading }) {
  const { selectedSite } = useSelectedSite();
  const total = data?.analytics.uniqueVisitors.total ?? 0;

  return (
    <div className="flex">
      <div className="w-1 grow border-r border-mystic-500">
        <TableHeader>Top channels</TableHeader>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          (data?.analytics.channels.data ?? []).slice(0, 3).map((channel, i) => (
            <TableRow key={channel.name} index={i + 1} className="!h-auto min-h-[55px] py-2">
              <div className="grow">{channel.name}</div>
              <div className="ml-4 flex w-14 shrink-0 items-center justify-end text-right">
                {total > 0 ? round((channel.visitors / total) * 100, 1).toLocaleString() : 0}%
              </div>
            </TableRow>
          ))
        )}
      </div>
      <div className="w-1 grow">
        <TableHeader>Top landing pages</TableHeader>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          (data?.analytics.entryPages.data ?? []).slice(0, 3).map((p, i) => (
            <TableRow key={`${p.domain}${p.name}`} index={i + 1}>
              <TruncateWithTooltip text={pageRowFormatter(selectedSite?.name)(p)} className="grow" />
              <div className="ml-4 flex w-16 shrink-0 items-center justify-end text-right">
                {total > 0 ? round((p.visitors / total) * 100, 1).toLocaleString() : 0}%
              </div>
            </TableRow>
          ))
        )}
      </div>
    </div>
  );
}
